import axiosInstance from './apiClient'
import { getErrorMessage } from '@/lib/utils'
import { isAxiosError, AxiosError } from 'axios'

export interface ThirdPartyApiKeyListItem {
  id: number
  key_prefix: string
  label: string
  player_email: string
  player_name: string
  created_at: string
  expires_at: string | null
  is_active: boolean
  is_expired: boolean
  last_used_at: string | null
}

export interface ThirdPartyApiKeyCreated extends ThirdPartyApiKeyListItem {
  raw_key: string
}

export function parseThirdPartyApiError(error: unknown): string {
  if (!isAxiosError(error)) {
    return getErrorMessage(error)
  }
  const data = error.response?.data as
    | Record<string, unknown>
    | string
    | undefined
  if (typeof data === 'string') return data
  if (data && typeof data === 'object') {
    if (typeof data.detail === 'string') return data.detail
    const parts: string[] = []
    for (const [key, value] of Object.entries(data)) {
      if (key === 'detail') continue
      if (Array.isArray(value) && value.length > 0 && typeof value[0] === 'string') {
        parts.push(value[0])
      } else if (typeof value === 'string') {
        parts.push(value)
      }
    }
    if (parts.length > 0) return parts.join(' ')
  }
  return (
    (error as AxiosError<{ error?: string }>).response?.data?.error ||
    error.message ||
    getErrorMessage(error)
  )
}

export async function listThirdPartyKeys(
  roomId: string
): Promise<ThirdPartyApiKeyListItem[]> {
  try {
    const response = await axiosInstance.get<ThirdPartyApiKeyListItem[]>(
      `/v1/rooms/${roomId}/keys/`
    )
    return response.data
  } catch (error) {
    throw new Error(parseThirdPartyApiError(error))
  }
}

export async function createThirdPartyKey(
  roomId: string,
  params: {
    player_id: number
    label: string
    expires_at?: string | null
  }
): Promise<ThirdPartyApiKeyCreated> {
  try {
    const query: Record<string, string | number> = {
      player_id: params.player_id,
      label: params.label,
    }
    if (params.expires_at) {
      query.expires_at = params.expires_at
    }
    const response = await axiosInstance.post<ThirdPartyApiKeyCreated>(
      `/v1/rooms/${roomId}/keys/`,
      null,
      { params: query }
    )
    return response.data
  } catch (error) {
    throw new Error(parseThirdPartyApiError(error))
  }
}

export async function revokeThirdPartyKey(
  roomId: string,
  keyPrefix: string
): Promise<void> {
  try {
    await axiosInstance.delete(`/v1/rooms/${roomId}/keys/${keyPrefix}/`)
  } catch (error) {
    throw new Error(parseThirdPartyApiError(error))
  }
}
